// Shared building blocks for all <i80-*> elements.
//
// Every element is a thin layer over real HTML in the light DOM: it adds the
// i80 classes from components.css to native elements (<button>, <input>, ...)
// that either the author wrote inside the tag or the element creates. That keeps
// forms, labels, focus and screen readers working as in plain HTML, and lets
// server-rendered markup (Django, Flask, HTMX) pass its own native elements in.

let uid = 0;

/** A document-unique id, for label/for and aria-describedby. */
export function nextId(prefix = "i80") {
  uid += 1;
  return `${prefix}-${uid}`;
}

/** Create an element with classes and attributes; null/false attributes are skipped. */
export function h(tag, { class: cls, ...attrs } = {}, ...children) {
  const el = document.createElement(tag);
  if (cls) el.className = Array.isArray(cls) ? cls.filter(Boolean).join(" ") : cls;
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined || v === false) continue;
    el.setAttribute(k, v === true ? "" : String(v));
  }
  for (const c of children.flat()) {
    if (c === null || c === undefined || c === false) continue;
    el.append(c instanceof Node ? c : document.createTextNode(String(c)));
  }
  return el;
}

/** The <i80-*> element a node belongs to. */
function hostOf(node) {
  for (let el = node.parentElement; el; el = el.parentElement) {
    if (el.localName.startsWith("i80-")) return el;
  }
  return null;
}

/**
 * Loose content: something the author put in the tag that we have not taken in yet. An
 * element we built is not loose, and neither is one that wraps a part we built — a
 * server-rendered <form> around our button belongs to the author, but it is in place.
 * A nested <i80-*> child has parts of its own; those don't count as ours.
 */
function isStray(host, el) {
  if (el.hasAttribute("data-i80-part")) return false;
  return ![...el.querySelectorAll("[data-i80-part]")].some((part) => hostOf(part) === host);
}

export class I80Element extends HTMLElement {
  /** Attributes that trigger update(). Subclasses list their own. */
  static get observedAttributes() {
    return [];
  }

  connectedCallback() {
    // setup() runs once per element instance; moving the element (HTMX swaps,
    // appendChild elsewhere) only calls update() again, so nothing is wrapped twice.
    if (!this._i80Ready) {
      this._i80Ready = true;
      this.setup();
    }
    this.update();
    this.watchLateChildren();
  }

  disconnectedCallback() {
    this._i80Watcher?.disconnect();
  }

  /**
   * An element is not always complete when the browser upgrades it: a template engine
   * (Ember, Vue) inserts the element and appends the children after it, and a streamed
   * server response does the same. Elements that collect children implement adopt() to
   * take in whatever turned up late.
   */
  watchLateChildren() {
    if (typeof this.adopt !== "function") return;
    // Moving the element (a parent adopting it) stops the observer and drops whatever it
    // had queued, so on every connect we both re-observe and take in what is already there.
    if (this._i80Watcher) {
      this._i80Watcher.observe(this, { childList: true });
      if (this.hasStrayContent()) {
        this.adopt();
        this.update();
      }
      return;
    }
    this._i80Watcher = new MutationObserver((records) => {
      const late = records.some((r) =>
        [...r.addedNodes].some((n) =>
          n.nodeType === Node.ELEMENT_NODE ? !n.hasAttribute("data-i80-part") : n.textContent.trim() !== ""));
      if (!late) return;
      this._i80Watcher.disconnect();
      try {
        this.adopt();
        this.update();
      } finally {
        if (this.isConnected) this._i80Watcher.observe(this, { childList: true });
      }
    });
    this._i80Watcher.observe(this, { childList: true });
  }

  attributeChangedCallback() {
    if (this._i80Ready) this.update();
  }

  /** Build or adopt the inner markup. Must be safe on markup it built before (clones). */
  setup() {}

  /** Sync classes and attributes from the element's own attributes. */
  update() {}

  // --- attribute helpers ---

  /** Boolean attribute: present and not "false". */
  flag(name) {
    const v = this.getAttribute(name);
    return v !== null && v !== "false";
  }

  /** String attribute limited to allowed values, falling back to the default (with a console hint). */
  choice(name, allowed, fallback) {
    const v = this.getAttribute(name);
    if (v === null || v === "") return fallback;
    if (allowed.includes(v)) return v;
    console.warn(`<${this.localName}> ${name}="${v}" is not one of: ${allowed.join(", ")}. Using "${fallback}".`);
    return fallback;
  }

  /** Direct child matching the selector (not a descendant of a nested element). */
  child(selector) {
    return [...this.children].find((c) => c.matches(selector)) ?? null;
  }

  /** The element's own part created by setup(), found again on clones. */
  part(name) {
    return this.querySelector(`[data-i80-part="${name}"]`);
  }

  /** Content sitting directly in the tag that we have not taken in yet. */
  hasStrayContent() {
    return [...this.childNodes].some((n) => (n.nodeType === Node.ELEMENT_NODE ? isStray(this, n) : n.nodeType === Node.TEXT_NODE && n.textContent.trim() !== ""));
  }

  /** Text/nodes the author wrote inside the tag, minus the parts we own. */
  takeContent(exclude = []) {
    const nodes = [];
    for (const n of [...this.childNodes]) {
      if (n.nodeType === Node.ELEMENT_NODE && (!isStray(this, n) || exclude.includes(n))) continue;
      if (n.nodeType === Node.TEXT_NODE && !n.textContent.trim()) {
        n.remove();
        continue;
      }
      nodes.push(n);
    }
    return nodes;
  }
}

/**
 * Text that arrived after the element was built (a template engine or a streamed
 * response appends it). Returns the new label, or null when nothing turned up.
 */
export function lateLabel(host) {
  const nodes = host.takeContent();
  if (!nodes.length) return null;
  const text = nodes.map((n) => n.textContent).join("").trim();
  for (const n of nodes) n.remove();
  return text || null;
}

/** Register an element once (safe if i80.js is loaded twice). */
/**
 * Register a tag.
 *
 * Order matters, and not only for tidiness: registering a name upgrades the elements
 * already on the page there and then, and their setup() may build other i80 elements. A
 * tag that is not registered yet comes back as a plain element with none of our methods,
 * so **a tag that another element builds must be registered before that element.**
 * `test/server-rendered.spec.js` holds the case that proves it.
 */
export function define(name, cls) {
  if (!customElements.get(name)) customElements.define(name, cls);
}
